import { useState, Fragment } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowUturnLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PermissionsContainer, { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { PermissionAwareInput, PermissionAwareSelect } from '../../../contexts/PermissionAccessContext';

/**
 * Who has to state a target delivery date when returning PROs.
 *
 * Everyone sees the field; only some have to fill it in.
 *
 * The ungranted default is Exempt (3): visible, editable, optional. An explicit
 * Edit (2) grant — the LCs — is what makes it mandatory. That is why the
 * default is stated as a level rather than left to the hidden/disabled/edit
 * ladder, which would resolve "no grant" to 2 and make it indistinguishable
 * from a real Edit grant.
 *
 * Exempt sitting *above* Edit also settles the dual-role case: grant it to the
 * Schedulers and someone who is both an LC and a Scheduler resolves to Exempt
 * under highest-wins, keeping an optional field. Hence `=== EDIT`, not
 * `>= EDIT`. View (1) shows it disabled and Hidden (0) removes it; neither is
 * expected here, but both drop the requirement.
 */
export const RETURN_TARGET_DATE_COMPONENT_ID = 'lc-return-target-date';
const ACCESS_LEVEL_EDIT = 2;
const ACCESS_LEVEL_EXEMPT = 3;

interface ReturnToSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * targetDate is '' when the caller isn't required to give one. targetOnOrBy
   * ('ON' | 'BY') always travels with the date: filled whenever targetDate is,
   * '' whenever it isn't.
   */
  onConfirm: (targetDate: string, comment: string, targetOnOrBy: string) => void;
  selectedCount: number;
  /** How many of the selected PROs already carry a target delivery date. */
  existingTargetCount: number;
  prepullOnlyCount?: number;
}

export default function ReturnToSchedulerModal({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  existingTargetCount,
  prepullOnlyCount = 0,
}: ReturnToSchedulerModalProps) {
  const [targetDate, setTargetDate] = useState('');
  const [targetOnOrBy, setTargetOnOrBy] = useState('');
  const [comment, setComment] = useState('');

  const targetDateAccess = useResolvedAccessLevel(
    RETURN_TARGET_DATE_COMPONENT_ID,
    false,
    false,
    ACCESS_LEVEL_EXEMPT,
  );
  const prepullOnly = selectedCount > 0 && prepullOnlyCount === selectedCount;
  const targetDateRequired = !prepullOnly && targetDateAccess === ACCESS_LEVEL_EDIT;
  // Whatever the grant, a date never travels without its ON/BY — a target
  // date with a blank targetonorby is the one state the PRO detail drawer
  // refuses to save (validateOrderSave Rule 12).
  const canConfirm = prepullOnly || (!targetDateRequired || !!targetDate) && (!targetDate || !!targetOnOrBy);

  const handleConfirm = () => {
    if (!canConfirm) return;
    // A selection left over from a since-cleared date stays behind — sent
    // alone it would overwrite the order's existing ON/BY.
    onConfirm(prepullOnly ? '' : targetDate, comment.trim(), !prepullOnly && targetDate ? targetOnOrBy : '');
    handleClose();
  };

  const handleClose = () => {
    setTargetDate('');
    setTargetOnOrBy('');
    setComment('');
    onClose();
  };

  // Alt+C closes. Confirm gets no chord — it moves the legs back to the
  // scheduler, which is a workflow hand-off rather than a save.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        aria-labelledby="return-to-scheduler-modal-title"
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel
                ref={panelRef}
                style={panelStyle}
                className="w-full max-w-md overflow-hidden rounded-xl bg-panel shadow-xl"
              >
                <ModalHeader
                  title="Return to Scheduler"
                  titleId="return-to-scheduler-modal-title"
                  onClose={handleClose}
                  dragHandleProps={dragHandleProps}
                />
                <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Return {selectedCount} selected PRO{selectedCount !== 1 ? 's' : ''} to the
                    scheduler. {prepullOnly
                      ? 'The pre-pull date will be cleared; the delivery appointment will be preserved.'
                      : 'The proposed delivery date will be cleared for orders without a confirmed delivery.'}
                  </p>

                  {prepullOnlyCount > 0 && !prepullOnly && (
                    <p className="text-sm text-muted mb-4">
                      {prepullOnlyCount} pre-pull return(s) will keep their confirmed delivery appointment and target date. Only their pre-pull date will be cleared.
                    </p>
                  )}
                  {!prepullOnly && <PermissionsContainer
                    componentId={RETURN_TARGET_DATE_COMPONENT_ID}
                    componentName="Return to Scheduler — Target Date"
                    className="mb-4"
                  >
                    <div className="flex gap-2">
                      <div className="w-24 flex-shrink-0">
                        <label
                          htmlFor="return-target-on-or-by"
                          className="block text-sm font-medium text-label mb-1"
                        >
                          On or By{' '}
                          {(targetDateRequired || !!targetDate) && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <PermissionAwareSelect
                          id="return-target-on-or-by"
                          value={targetOnOrBy}
                          onChange={(e) => setTargetOnOrBy(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        >
                          <option value=""></option>
                          <option value="ON">ON</option>
                          <option value="BY">BY</option>
                        </PermissionAwareSelect>
                      </div>
                      <div className="flex-1">
                        <label
                          htmlFor="return-target-date"
                          className="block text-sm font-medium text-label mb-1"
                        >
                          Target delivery date{' '}
                          {targetDateRequired && <span className="text-red-500">*</span>}
                        </label>
                        <PermissionAwareInput
                          id="return-target-date"
                          type="date"
                          value={targetDate}
                          onChange={(e) => setTargetDate(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted mt-1">
                      The date the scheduler should aim for — delivered ON it or BY
                      it. Written only to selected PROs without a confirmed delivery.
                    </p>
                    {existingTargetCount > 0 && (
                      <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400 mt-2">
                        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-px" />
                        <span>
                          {existingTargetCount} of the selected PRO
                          {existingTargetCount !== 1 ? 's' : ''} already{' '}
                          {existingTargetCount !== 1 ? 'have' : 'has'} a target date — it
                          will be overwritten.
                        </span>
                      </p>
                    )}
                  </PermissionsContainer>}

                  <div>
                    <label
                      htmlFor="return-comment"
                      className="block text-sm font-medium text-label mb-1"
                    >
                      Comment for the scheduler (optional)
                    </label>
                    <textarea
                      id="return-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      placeholder="e.g. needs prepull 7/28"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <p className="text-xs text-muted mt-1">
                      Appended with a timestamp to the scheduling comments of every selected PRO.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    title={
                      canConfirm
                        ? undefined
                        : targetDate
                          ? 'Select ON or BY for the target delivery date'
                          : 'Enter a target delivery date first'
                    }
                    aria-describedby={canConfirm ? undefined : 'return-target-date'}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                  >
                    <ArrowUturnLeftIcon className="w-4 h-4" />
                    Return {selectedCount} PRO{selectedCount !== 1 ? 's' : ''}
                  </button>
                </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
